import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from '@mui/styles';

import {
  ReferralMappingOperationType,
  ReferralMappingRosterType,
} from '@vestahealthcare/common/enums';
import { Selectable, SelectableInfo } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  Organization,
  ReferralIngestionMapperColumn,
  ReferralIngestionMapperSourceColumn,
  ReferralMapping,
  ReferralMappingColumn,
  ReferralMappingOperation,
} from '@vestahealthcare/common/models';
import { ReferralMappingMappedValue } from '@vestahealthcare/common/models/ReferralMappingMappedValue';

import {
  Arrow,
  BackgroundColors,
  Button,
  CheckboxWithLabel,
  Colors,
  IconButton,
  Modal,
  PanelInfo,
  Select,
  Tabs,
  TextInput,
} from 'styleguide-v2';

import {
  CreateRosterFileHistoryMapper,
  UpdateRosterFileHistoryMapper,
  readRosterFileHistoryHeaderValues,
  readRosterFileHistoryHeaders,
} from 'dash/src/services/FileHistoryServices';
import { excludeOtherValues } from 'dash/src/utils/dataUtils';

import { showGlobalError } from '../../../components/GlobalMessage';
import { CacheServices } from '../../../services';
import { MappingRuleRow } from './MappingRuleRow';

export type Rule = Partial<ReferralMappingColumn> & {
  default: boolean;
  required: boolean;
  sort: number;
  readonly?: boolean;
  candidateIndex?: number;
  rosterType?: string;
};

type Props = {
  data: {
    destinationColumns: SelectableInfo<ReferralIngestionMapperColumn>[];
    files: Selectable[];
    organizations: SelectableInfo<Organization>[];
  };
  loadingData: boolean;
  open: boolean;
  onClose: () => void;
  onSubmit: (
    params: CreateRosterFileHistoryMapper | UpdateRosterFileHistoryMapper,
    id?: number,
  ) => Promise<void>;
  rule?: ReferralMapping;
};

export const useStyles = makeStyles({
  activeSwitch: {
    marginLeft: '0.5rem',
  },
  addItemLink: {
    color: Colors.textGreen,
    fontSize: '0.875em',
    marginLeft: '0.5rem',
    textDecoration: 'underline',

    '&:hover': {
      color: Colors.iconGreen,
    },
  },
  addItemIcon: {
    marginTop: '-0.25rem',
  },
  addTransformationLink: {
    color: Colors.textGreen,
    fontSize: '0.875em',
    marginLeft: '0.5rem',

    '&:hover': {
      color: Colors.iconGreen,
    },
  },
  button: {
    width: '30rem',
  },
  fileInputLarge: {
    flex: '1',
    minWidth: '25rem',
  },
  fileInputSmall: {
    flex: '0',
    minWidth: '10rem',
  },
  firtsItem: {
    '&&': {
      marginTop: '2.1rem',
    },
  },
  footerCheckbox: {
    '&& .MuiCheckbox-root': {
      paddingLeft: '0.5rem',
      paddingTop: '0.5rem',
    },
  },
  row: {
    gap: '1rem',
    padding: '0 0 0.5rem 1rem',
  },
  rowMappingContainer: {
    margin: '0 3.5rem 2rem',
    padding: '0 1.5rem',
    position: 'relative',
  },
  rowMappingSplitContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: '60rem',
  },
  rowMappingContainerBorder: {
    '&::before': {
      borderLeft: `2px solid ${BackgroundColors.grayLine}`,
      content: '""',
      top: '3rem',
      height: 'calc(100% - 4.5rem)',
      left: 0,
      position: 'absolute',
    },
  },
  select: {
    width: '22.5rem',
  },
  selectLarge: {
    width: '25rem',
  },
  selectExtraLarge: {
    width: '30rem',
  },
  selectExtraExtraMin: {
    width: '5rem',
  },
  selectExtraMin: {
    width: '12rem',
    '&& .MuiAutocomplete-root .MuiFormControl-root .MuiOutlinedInput-root': {
      paddingRight: '1.5rem',
    },
  },
  selectMin: {
    width: '15rem',

    '&& .MuiAutocomplete-root .MuiFormControl-root .MuiOutlinedInput-root': {
      paddingRight: '1.5rem',
    },
  },
  tabsContainer: {
    marginTop: '',
    marginBottom: '1rem',
    borderBottom: `1px solid ${BackgroundColors.grayLine}`,
  },
});

const CANDIDATE_ELIGIBLE_COLUMNS = [
  'Referral Source',
  'First Name',
  'Last Name',
  'External Id',
];

const MODAL_TAB_MEMBER = 'members';
const MODAL_TAB_CANDIDATE = 'ctCandidates';

const TABS = [
  {
    value: MODAL_TAB_MEMBER,
    label: translate('referrals.ingestionMapper.tabs.members'),
  },
  {
    value: MODAL_TAB_CANDIDATE,
    label: translate('referrals.ingestionMapper.tabs.candidates'),
  },
];

export const ReferralIngestionMapperEditModal = ({
  data,
  loadingData,
  open,
  onClose,
  onSubmit,
  rule,
}: Props) => {
  const styles = useStyles();

  const [loadingFileHeaders, setLoadingFileHeaders] = useState<boolean>(false);
  const [loadingFileBranchValues, setLoadingFileBranchValues] = useState<
    boolean
  >(false);
  const [loadingFileGenderValues, setLoadingFileGenderValues] = useState<
    boolean
  >(false);
  const [loadingFileLanguageValues, setLoadingFileLanguageValues] = useState<
    boolean
  >(false);

  const [errorBranchNotFound, setErrorBranchNotFound] = useState<boolean>(
    false,
  );

  const [submitted, setSubmitted] = useState<boolean>(false);

  const [candidateValidationErrors, setCandidateValidationErrors] = useState<
    string[]
  >([]);

  const [active, setActive] = useState<boolean>(false);
  const [duplicateMembers, setDuplicateMembers] = useState<boolean>(false);

  const [separator, setSeparator] = useState<string>(',');
  const [file, setFile] = useState<Selectable>();
  const [mappingRules, setMappingRules] = useState<Rule[]>([]);
  const [mixedFile, setMixedFile] = useState<boolean>();
  const [name, setName] = useState<string>();
  const [referral, setReferral] = useState<SelectableInfo<Organization>[]>([]);
  const [sourceFileHeaders, setSourceFileHeaders] = useState<
    ReferralIngestionMapperSourceColumn[]
  >([]);
  const [sourceFileBranches, setSourceFileBranches] = useState<string[]>([]);
  const [sourceFileGenders, setSourceFileGenders] = useState<string[]>([]);
  const [sourceFileLanguages, setSourceFileLanguages] = useState<string[]>([]);
  const [booleanColumnsData, setBooleanColumnsData] = useState<
    Record<string, string[]>
  >({});
  const [loadingBooleanColumns, setLoadingBooleanColumns] = useState<
    Record<string, boolean>
  >({});

  const [relationships, setRelationships] = useState<SelectableInfo<any>[]>([]);
  const [relationshipColumnsData, setRelationshipColumnsData] = useState<
    Record<string, string[]>
  >({});
  const [loadingRelationshipColumns, setLoadingRelationshipColumns] = useState<
    Record<string, boolean>
  >({});

  const [activeTab, setActiveTab] = useState<string>(MODAL_TAB_MEMBER);
  const [candidateTotal, setCandidateTotal] = useState<number>(0);
  const [ctCandidateMappingRules, setCtCandidateMappingRules] = useState<
    Rule[]
  >([]);

  const scrollRef = useRef<HTMLDivElement>(null);

  const branchColumn = useMemo(
    () =>
      mappingRules?.find(
        ({ operation }) =>
          operation?.action === ReferralMappingOperationType.BRANCH,
      ),
    [mappingRules],
  );

  const genderColumn = useMemo(
    () =>
      mappingRules?.find(
        ({ operation }) =>
          operation?.action === ReferralMappingOperationType.GENDER,
      ),
    [mappingRules],
  );

  const languageColumn = useMemo(
    () =>
      mappingRules?.find(
        ({ operation }) =>
          operation?.action === ReferralMappingOperationType.LANGUAGE,
      ),
    [mappingRules],
  );

  const splitMultipleColumn = useMemo(
    () =>
      mappingRules?.find(
        ({ operation }) =>
          operation?.action === ReferralMappingOperationType.SPLIT_MULTIPLE,
      ),
    [mappingRules],
  );

  const showMixedFileCheckbox = useMemo(
    () =>
      file &&
      referral?.some((item) =>
        item.info?.children?.some(({ hasValidDate }) => hasValidDate),
      ),
    [referral, file],
  );

  const mixedReferrals = useMemo(
    () =>
      showMixedFileCheckbox
        ? Organization.toSelectable(
            referral.reduce(
              (acc, item) =>
                [
                  ...acc,
                  ...(item.info?.children?.filter(
                    ({ hasValidDate }) => hasValidDate,
                  ) || []),
                ].filter(Boolean),
              [] as Organization[],
            ),
          )
        : [],
    [referral, showMixedFileCheckbox],
  );

  const buildRelationships = async () => {
    const [
      types,
      familyRelationships,
      aideTypes,
      otherRelationships,
      specialities,
    ] = await Promise.all([
      CacheServices.getCareTeamMemberTypes(),
      CacheServices.getCareTeamMemberFamilyRelationShips(),
      CacheServices.getCareTeamMemberAideTypes(),
      CacheServices.getCareTeamMemberOtherRelationships(),
      CacheServices.getCareTeamMemberProviderSpecialities(),
    ]);

    const typeGroups = types.reduce((acc, type) => {
      acc[type.id] = type.description;
      return acc;
    }, {} as Record<string, string>);

    const formattedRelationships = [
      ...familyRelationships.map((rel) => ({
        value: rel.id,
        label: rel.description,
        info: { group: typeGroups?.FAMILY },
      })),
      ...aideTypes.map((aide) => ({
        value: aide.id,
        label: aide.description,
        info: { group: typeGroups?.AIDE },
      })),
      ...otherRelationships.map((other) => ({
        value: other.id,
        label: other.description,
        info: { group: typeGroups?.OTHER },
      })),
      ...specialities.map((spec) => ({
        value: spec.id,
        label: spec.description,
        info: { group: typeGroups?.PROVIDER },
      })),
    ];

    setRelationships(formattedRelationships);
  };

  const hasMemberIdentificationFields = useMemo(() => {
    const hasFirstName = mappingRules?.some(
      ({ targetColumn, sourceColumn }) =>
        targetColumn === 'First Name' && sourceColumn,
    );
    const hasLastName = mappingRules?.some(
      ({ targetColumn, sourceColumn }) =>
        targetColumn === 'Last Name' && sourceColumn,
    );

    const hasReferralSource = mappingRules?.some(
      ({ targetColumn, sourceColumn }) =>
        targetColumn === 'Referral Source' && sourceColumn,
    );

    return hasReferralSource && hasFirstName && hasLastName;
  }, [mappingRules]);

  const availableCtSourceColumns = useMemo(() => {
    const usedColumns =
      mappingRules
        ?.filter(({ sourceColumn }) => sourceColumn)
        ?.map(({ sourceColumn, sourceColumnIndex }) => ({
          sourceColumn,
          sourceColumnIndex,
        })) || [];
    return sourceFileHeaders?.filter(
      (header) =>
        !usedColumns.find(
          ({ sourceColumn, sourceColumnIndex }) =>
            sourceColumn === header.name && sourceColumnIndex === header.index,
        ),
    );
  }, [sourceFileHeaders, mappingRules]);

  useEffect(() => {
    if (!file || !separator) {
      setSourceFileHeaders([]);
    } else {
      setLoadingFileHeaders(true);
      readRosterFileHistoryHeaders(file.value as number, separator)
        .then(setSourceFileHeaders)
        .finally(() => setLoadingFileHeaders(false));
    }
  }, [file, separator]);

  const updateFileValues = async (
    ruleAccessor: 'branchColumn' | 'genderColumn' | 'languageColumn',
    setterValues: React.Dispatch<React.SetStateAction<string[]>>,
    setterLoading: (value: React.SetStateAction<boolean>) => void,
    file?: Selectable,
    modelColumn?: Rule,
  ) => {
    if (file && modelColumn?.sourceColumn) {
      setterLoading(true);
      await readRosterFileHistoryHeaderValues(
        file.value as number,
        modelColumn.sourceColumn,
        modelColumn.sourceColumnIndex || 0,
        separator,
      )
        .then((items) => {
          const itemsNotIncluded =
            rule?.[ruleAccessor]?.operation?.dataDictionary
              ?.map((item) => item.sourceValue)
              ?.filter((key) => !items.includes(key)) || [];
          setterValues([...itemsNotIncluded, ...items]);
          return items;
        })
        .then((items) => {
          const column = rule ? rule[ruleAccessor] : undefined;
          if (modelColumn?.operation) {
            if (
              items?.length &&
              column?.sourceColumn === modelColumn.sourceColumn
            ) {
              modelColumn.operation.dataDictionary = column?.operation
                ?.dataDictionary || [new ReferralMappingMappedValue({})];
            } else {
              modelColumn.operation.dataDictionary = [
                new ReferralMappingMappedValue({}),
              ];
            }
          }
        })
        .then(() => setterLoading(false));
    } else {
      setterValues([]);
    }
  };

  const updateMultipleFileValues = async (
    sourceColumn: string,
    sourceColumnIndex: number,
    modelColumn: Rule,
    setterLoading: (
      value: React.SetStateAction<Record<string, boolean>>,
    ) => void,
    setterColumnsData: (
      value: React.SetStateAction<Record<string, string[]>>,
    ) => void,
    actionType: ReferralMappingOperationType,
    file?: Selectable,
  ) => {
    if (file && sourceColumn) {
      setterLoading?.(
        (prev) =>
          ({ ...prev, [sourceColumn]: true } as Record<string, boolean>),
      );
      try {
        const items = await readRosterFileHistoryHeaderValues(
          file.value as number,
          sourceColumn,
          sourceColumnIndex,
          separator,
        );

        setterColumnsData((prev) => ({
          ...prev,
          [sourceColumn]: items,
        }));

        if (modelColumn?.operation) {
          const existingRule = rule?.columns?.find(
            (col) =>
              col.sourceColumn === sourceColumn &&
              col.operation?.action === actionType &&
              col.rosterType === modelColumn.rosterType,
          );

          if (items?.length && existingRule?.operation?.dataDictionary) {
            modelColumn.operation.dataDictionary =
              existingRule.operation.dataDictionary;
          } else {
            modelColumn.operation.dataDictionary = [
              new ReferralMappingMappedValue({}),
            ];
          }
        }
      } finally {
        setterLoading((prev) => ({
          ...prev,
          [sourceColumn]: false,
        }));
      }
    }
  };

  useEffect(() => {
    if (mixedFile && open && separator) {
      updateFileValues(
        'branchColumn',
        setSourceFileBranches,
        setLoadingFileBranchValues,
        file,
        branchColumn,
      );
    } else {
      setSourceFileBranches([]);
    }
  }, [file, mixedFile, branchColumn, separator, open]);

  useEffect(() => {
    if (branchColumn && open) {
      setErrorBranchNotFound(false);
    }
  }, [branchColumn, open]);

  useEffect(() => {
    if (open && separator) {
      updateFileValues(
        'genderColumn',
        setSourceFileGenders,
        setLoadingFileGenderValues,
        file,
        genderColumn,
      );
    }
  }, [file, genderColumn, separator, open]);

  useEffect(() => {
    if (open && separator) {
      updateFileValues(
        'languageColumn',
        setSourceFileLanguages,
        setLoadingFileLanguageValues,
        file,
        languageColumn,
      );
    }
  }, [file, languageColumn, separator, open]);

  useEffect(() => {
    if (!showMixedFileCheckbox) {
      setMixedFile(false);
      if (branchColumn) {
        delete branchColumn.operation;
        setMappingRules([...mappingRules]);
      }
    }
  }, [showMixedFileCheckbox]);

  useEffect(() => {
    buildRelationships();
  }, []);

  useEffect(() => {
    const candidateColumns =
      (rule?.columns?.filter(
        ({ rosterType }) =>
          rosterType === ReferralMappingRosterType.CANDIDATE.value,
      ) as Rule[]) || [];

    if (
      open &&
      separator &&
      file &&
      candidateColumns?.length > 0 &&
      rule?.columns?.length
    ) {
      candidateColumns.forEach((rule) => {
        if (
          rule.operation?.action === ReferralMappingOperationType.BOOLEAN &&
          rule.sourceColumn &&
          !loadingBooleanColumns[rule.sourceColumn]
        ) {
          updateMultipleFileValues(
            rule.sourceColumn,
            rule.sourceColumnIndex || 0,
            rule,
            setLoadingBooleanColumns,
            setBooleanColumnsData,
            ReferralMappingOperationType.BOOLEAN,
            file,
          );
        }
      });

      candidateColumns.forEach((rule) => {
        if (
          rule.operation?.action ===
            ReferralMappingOperationType.RELATIONSHIP &&
          rule.sourceColumn &&
          !loadingRelationshipColumns[rule.sourceColumn]
        ) {
          updateMultipleFileValues(
            rule.sourceColumn,
            rule.sourceColumnIndex || 0,
            rule,
            setLoadingRelationshipColumns,
            setRelationshipColumnsData,
            ReferralMappingOperationType.RELATIONSHIP,
            file,
          );
        }
      });
    }
  }, [open, separator, file, rule?.columns]);

  const transformSplitToMultiple = (rule: Rule) => {
    let { operation } = rule;

    if (rule.operation?.action === ReferralMappingOperationType.SPLIT) {
      operation = {
        ...rule.operation,
        action: ReferralMappingOperationType.SPLIT_MULTIPLE,
        param1: rule.operation.param1,
        param2: '',
        dataDictionary: [
          new ReferralMappingMappedValue({
            sourceValue: rule.operation.param1,
            destinationValue: rule.operation.param2,
          }),
        ],
      };
    } else if (
      rule.operation?.action === ReferralMappingOperationType.SPLIT_MULTIPLE
    ) {
      operation = {
        ...rule.operation,
        param1: rule.operation.param1,
      };
    }
    return {
      ...rule,
      operation,
    };
  };

  useEffect(() => {
    if (rule?.columns?.length) {
      const memberColumns = rule.columns.filter(
        ({ rosterType }) =>
          rosterType === ReferralMappingRosterType.MEMBER.value || !rosterType,
      );
      const candidateColumns = rule.columns.filter(
        ({ rosterType }) =>
          rosterType === ReferralMappingRosterType.CANDIDATE.value,
      );

      const mappingRules =
        memberColumns
          .filter(
            ({ sourceColumn, sourceColumnIndex, operation }) =>
              sourceFileHeaders.find(
                (header) =>
                  sourceColumn === header.name &&
                  sourceColumnIndex === header.index,
              ) || operation?.action === ReferralMappingOperationType.DEFAULT,
          )
          .map(
            (column) =>
              ({
                ...column,
                default:
                  column?.operation?.action ===
                  ReferralMappingOperationType.DEFAULT,
                defaultValue:
                  column?.operation?.action ===
                  ReferralMappingOperationType.DEFAULT
                    ? column.operation.param1
                    : undefined,
                operation:
                  column?.operation?.action ===
                  ReferralMappingOperationType.DEFAULT
                    ? undefined
                    : column.operation,
                required: !!data.destinationColumns.find(
                  ({ value }) => value === column.targetColumn,
                )?.info?.required,
              } as Rule),
          )
          .map(transformSplitToMultiple)
          .sort(({ required: a }, { required: b }) => {
            if (a && !b) return -1;
            if (!a && b) return 1;
            return 0;
          }) || [];
      const mappingRulesNames = mappingRules.map((item) => item.targetColumn);
      const requiredRules = data.destinationColumns
        .filter(
          ({ info }) =>
            info?.required && !mappingRulesNames.includes(info?.name),
        )
        .map(
          ({ info }) =>
            ({
              targetColumn: info?.name || '',
              required: !!info?.required,
              operation: info?.transformation,
            } as Rule),
        )
        .map(transformSplitToMultiple);
      setMappingRules([...mappingRules, ...requiredRules]);
      setCtCandidateMappingRules(
        candidateColumns
          .filter(
            ({ sourceColumn, sourceColumnIndex, operation }) =>
              sourceFileHeaders.find(
                (header) =>
                  sourceColumn === header.name &&
                  sourceColumnIndex === header.index,
              ) || operation?.action === ReferralMappingOperationType.DEFAULT,
          )
          .map(
            (column) =>
              ({
                ...column,
                default:
                  column?.operation?.action ===
                  ReferralMappingOperationType.DEFAULT,
                defaultValue:
                  column?.operation?.action ===
                  ReferralMappingOperationType.DEFAULT
                    ? column.operation.param1
                    : undefined,
                operation:
                  column?.operation?.action ===
                  ReferralMappingOperationType.DEFAULT
                    ? undefined
                    : column.operation,
                required: false,
              } as Rule),
          )
          .map(transformSplitToMultiple) || [],
      );
    } else {
      setMappingRules(
        data.destinationColumns?.length
          ? data.destinationColumns
              .filter(({ info }) => info?.required)
              .map(
                ({ info }) =>
                  ({
                    targetColumn: info?.name || '',
                    required: !!info?.required,
                    operation: info?.transformation,
                  } as Rule),
              )
              .map(transformSplitToMultiple)
          : [],
      );
      setCtCandidateMappingRules([]);
    }
  }, [sourceFileHeaders, data.destinationColumns, rule?.columns]);

  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setCandidateValidationErrors([]);
    }
  }, [open]);

  useEffect(() => {
    setActive(!!rule?.active);
    setDuplicateMembers(!!rule?.duplicateMembers);
    setName(rule?.name || '');
    setSeparator(rule?.separator || ',');
    const orgIds = rule?.organizations?.map(({ id }) => id);
    setReferral(
      orgIds
        ? data.organizations?.filter(({ value }) =>
            orgIds?.includes(value as number),
          )
        : [],
    );
    setFile(
      rule?.file?.id
        ? data.files?.find(({ value }) => value === rule?.file?.id)
        : undefined,
    );
    if (rule?.branchColumn) {
      setMixedFile(true);
    } else {
      setMixedFile(false);
    }
    setCandidateTotal(rule?.candidateTotal || 0);
  }, [rule, data.files, data.organizations]);

  useEffect(() => {
    if (
      mappingRules?.length &&
      activeTab === MODAL_TAB_CANDIDATE &&
      hasMemberIdentificationFields
    ) {
      const defaultCtRules =
        mappingRules
          ?.filter(
            ({ sourceColumn, targetColumn }) => sourceColumn && targetColumn,
          )
          ?.filter(
            ({ targetColumn }) =>
              targetColumn && CANDIDATE_ELIGIBLE_COLUMNS.includes(targetColumn),
          )
          ?.map(
            ({ sourceColumn, targetColumn }) =>
              ({
                sourceColumn,
                targetColumn: `Member ${targetColumn}`,
                default: false,
                required: false,
                sort: 0,
                readonly: true,
                rosterType: ReferralMappingRosterType.CANDIDATE.value,
              } as Rule),
          ) || [];

      const existingTargets = ctCandidateMappingRules.map(
        ({ targetColumn }) => targetColumn,
      );
      const newDefaults = defaultCtRules.filter(
        ({ targetColumn }) => !existingTargets.includes(targetColumn),
      );

      if (newDefaults.length > 0) {
        setCtCandidateMappingRules([
          ...newDefaults,
          ...ctCandidateMappingRules,
        ]);
      }
    }
  }, [
    mappingRules.length,
    activeTab,
    ctCandidateMappingRules.length,
    hasMemberIdentificationFields,
  ]);

  const validateCandidateRequirements = () => {
    const errors: string[] = [];

    if (candidateTotal === 0) {
      setCandidateValidationErrors([]);
      return { isValid: true, errors: [] };
    }

    const candidateRulesByIndex: Record<number, Rule[]> = {};

    ctCandidateMappingRules
      ?.filter((rule) => !rule.readonly && rule.candidateIndex)
      ?.forEach((rule) => {
        const candidateIndex = rule.candidateIndex || 0;
        if (!candidateRulesByIndex[candidateIndex]) {
          candidateRulesByIndex[candidateIndex] = [];
        }
        candidateRulesByIndex[candidateIndex].push(rule);
      });

    // eslint-disable-next-line no-plusplus
    for (let i = 1; i <= candidateTotal; i++) {
      const candidateRules = candidateRulesByIndex[i] || [];

      const hasFirstName = candidateRules.some(
        (rule) =>
          rule.targetColumn?.includes('First Name') &&
          (rule.sourceColumn || rule.defaultValue),
      );

      if (!hasFirstName) {
        errors.push(
          translate('referrals.ingestionMapper.modal.candidateNameError', {
            index: i,
          }),
        );
      }
    }
    setCandidateValidationErrors(errors);
    return { isValid: errors.length === 0, errors };
  };

  const checkRules = () =>
    mappingRules?.every(
      ({ sourceColumn, targetColumn, operation, defaultValue }) =>
        (sourceColumn || defaultValue) &&
        targetColumn &&
        (!operation ||
          (operation.action &&
            (operation.action !== ReferralMappingOperationType.SPLIT_MULTIPLE ||
              operation.param1))),
    );

  const checkCtCandidateRules = () =>
    ctCandidateMappingRules
      ?.filter((rule) => !rule.readonly)
      ?.every(
        ({ sourceColumn, targetColumn, operation, defaultValue }) =>
          (sourceColumn || defaultValue) &&
          targetColumn &&
          (!operation ||
            (operation.action &&
              (operation.action !==
                ReferralMappingOperationType.SPLIT_MULTIPLE ||
                operation.param1))),
      ) ?? true;

  const checkMixedRules = () =>
    branchColumn?.sourceColumn &&
    branchColumn?.operation?.dataDictionary?.every(
      ({ sourceValue, destinationValue }) => destinationValue && sourceValue,
    );

  const checkCustomRules = (
    column?: string,
    mixedRules?: ReferralMappingMappedValue[],
  ) =>
    !column ||
    (mixedRules?.length &&
      mixedRules?.every(
        ({ sourceValue, destinationValue }) => destinationValue && sourceValue,
      ));

  const validate = () =>
    name &&
    referral?.length &&
    file?.value &&
    separator &&
    checkRules() &&
    checkCtCandidateRules() &&
    validateCandidateRequirements().isValid &&
    (!mixedFile || checkMixedRules()) &&
    checkCustomRules(
      genderColumn?.sourceColumn,
      genderColumn?.operation?.dataDictionary,
    ) &&
    checkCustomRules(
      languageColumn?.sourceColumn,
      languageColumn?.operation?.dataDictionary,
    ) &&
    checkCustomRules(
      splitMultipleColumn?.sourceColumn,
      splitMultipleColumn?.operation?.dataDictionary,
    );

  const getServerOperation = (uiRule: Rule) => {
    if (uiRule?.default) {
      return {
        action: ReferralMappingOperationType.DEFAULT.value,
        param1: uiRule?.defaultValue,
      };
    }
    if (uiRule?.operation) {
      return {
        action: uiRule?.operation?.action?.value || '',
        param1: uiRule?.operation?.param1,
        param2: uiRule?.operation?.param2,
        dataDictionary: uiRule.operation.dataDictionary?.reduce(
          (acc, uiRule) => ({
            ...acc,
            [uiRule.sourceValue || '']: uiRule.destinationValue || '',
          }),
          {},
        ),
      };
    }
    return undefined;
  };

  const getAddToCandidate = (
    rosterType: string,
    targetColumn: string,
  ): boolean => {
    if (rosterType === ReferralMappingRosterType.CANDIDATE.value) {
      return true;
    }

    if (rosterType === ReferralMappingRosterType.MEMBER.value) {
      return CANDIDATE_ELIGIBLE_COLUMNS.includes(targetColumn);
    }

    return false;
  };

  const doSubmit = async () => {
    setSubmitted(true);

    const candidateValidation = validateCandidateRequirements();
    if (candidateValidation.errors?.length) {
      scrollRef.current?.scrollTo({ top: 190, behavior: 'smooth' });
    }

    if (!validate()) {
      if (mixedFile && !branchColumn) setErrorBranchNotFound(true);

      if (candidateValidation.errors.length > 0 || !checkCtCandidateRules()) {
        setActiveTab(MODAL_TAB_CANDIDATE);
        return false;
      }
      setActiveTab(MODAL_TAB_MEMBER);
      return false;
    }

    try {
      await onSubmit(
        {
          active,
          duplicateMembers,
          name: name || '',
          organizationIds: referral?.map(({ value }) => value as number),
          fileId: file?.value as number,
          candidateTotal,
          columns: [
            ...(mappingRules || []).map((rule) => ({
              id: rule?.id,
              sourceColumn: rule?.default ? undefined : rule.sourceColumn,
              sourceColumnIndex: rule?.default
                ? undefined
                : rule.sourceColumnIndex ?? 0,
              targetColumn: rule?.targetColumn,
              operation: getServerOperation(rule),
              rosterType:
                rule?.rosterType || ReferralMappingRosterType.MEMBER.value,
              addToCandidate: getAddToCandidate(
                rule?.rosterType || ReferralMappingRosterType.MEMBER.value,
                rule?.targetColumn || '',
              ),
            })),
            ...(ctCandidateMappingRules || [])
              .filter((rule) => !rule.readonly)
              .map((rule) => ({
                id: rule?.id,
                sourceColumn: rule?.default ? undefined : rule.sourceColumn,
                sourceColumnIndex: rule?.default
                  ? undefined
                  : rule.sourceColumnIndex ?? 0,
                targetColumn:
                  rule?.targetColumn?.replace(
                    /^Candidate \d+ /,
                    'Candidate ',
                  ) || '',
                operation: getServerOperation(rule),
                rosterType:
                  rule?.rosterType || ReferralMappingRosterType.CANDIDATE.value,
                candidateIndex: rule?.candidateIndex || 0,
                addToCandidate: getAddToCandidate(
                  rule?.rosterType || ReferralMappingRosterType.CANDIDATE.value,
                  rule?.targetColumn || '',
                ),
              })),
          ],
          separator,
        },
        rule?.id,
      );
      setSubmitted(false);
      setCandidateValidationErrors([]);
    } catch (e) {
      showGlobalError(e as string);
      return false;
    }
    return true;
  };

  const getSourceItems = (
    sourceItems = [] as string[],
    dataDictionary = [] as ReferralMappingMappedValue[],
  ) => [
    ...sourceItems,
    ...dataDictionary
      .filter(
        (value) =>
          value.sourceValue && !sourceItems.includes(value.sourceValue),
      )
      .map((value) => value.sourceValue),
  ];

  const renderMappingOptions = (
    operation: ReferralMappingOperation,
    sourceItems: string[],
    destinationItemLabel: string,
    destinationItems: SelectableInfo<any>[],
    destinationItemsGrouped?: any,
    keySuffix?: string,
    rosterType: string = ReferralMappingRosterType.MEMBER.value,
  ) => (
    <div
      className={classNames(
        'grid-span-12',
        styles.rowMappingContainer,
        sourceItems?.length && styles.rowMappingContainerBorder,
      )}
    >
      {!!sourceItems?.length &&
        !!operation &&
        operation?.dataDictionary?.map((data, idx) => (
          <div
            className={classNames('flex middle', styles.row)}
            key={`referral-mixed-ingestion-rule-${idx}-${keySuffix || 'items'}`}
          >
            <Select
              className={styles.selectLarge}
              disableClearable
              error={submitted && !data.sourceValue}
              getItemLabel={(item: { value: string }) => item.value}
              items={excludeOtherValues<{ value: string }>(
                getSourceItems(sourceItems, operation?.dataDictionary)
                  .toSorted((a, b) => a.localeCompare(b))
                  .map((value) => ({ value, label: value })),
                operation?.dataDictionary.map(({ sourceValue }) => ({
                  value: sourceValue || '',
                })) || [],
                { value: data.sourceValue || '' },
              )}
              label={
                idx === 0 ? (
                  <span className="flex middle" style={{ height: '17.14px' }}>
                    {translate('referrals.ingestionMapper.modal.source')}
                    {' * '}
                    {(operation?.dataDictionary?.length || 0) <
                      getSourceItems(sourceItems, operation?.dataDictionary)
                        ?.length && (
                      <button
                        className={classNames(
                          'flex middle',
                          styles.addItemLink,
                        )}
                        onClick={(evt) => {
                          evt.preventDefault();
                          if (operation) {
                            operation.dataDictionary = [
                              ...operation.dataDictionary,
                              new ReferralMappingMappedValue({}),
                            ];
                            if (
                              rosterType ===
                              ReferralMappingRosterType.MEMBER.value
                            ) {
                              setMappingRules([...mappingRules]);
                            } else {
                              setCtCandidateMappingRules([
                                ...ctCandidateMappingRules,
                              ]);
                            }
                          }
                        }}
                      >
                        <AddIcon
                          className={styles.addItemIcon}
                          fontSize="small"
                        />
                        {translate('referrals.ingestionMapper.modal.addSource')}
                      </button>
                    )}
                  </span>
                ) : undefined
              }
              placeholder={translate('referrals.ingestionMapper.modal.source')}
              onChange={(sourceValue: { value: string }) => {
                if (operation) {
                  operation.dataDictionary[idx].sourceValue = sourceValue.value;
                  if (rosterType === ReferralMappingRosterType.MEMBER.value) {
                    setMappingRules([...mappingRules]);
                  } else {
                    setCtCandidateMappingRules([...ctCandidateMappingRules]);
                  }
                }
              }}
              value={data.sourceValue ? { value: data.sourceValue } : undefined}
              size="small"
            />
            <Arrow className={classNames(idx === 0 && styles.firtsItem)} />
            <Select
              className={styles.selectExtraLarge}
              disableClearable
              error={submitted && !data.destinationValue}
              items={destinationItems}
              grouped={destinationItemsGrouped}
              label={idx === 0 ? destinationItemLabel : undefined}
              placeholder={destinationItemLabel}
              onChange={(destinationValue: Selectable) => {
                if (operation) {
                  operation.dataDictionary[idx].destinationValue =
                    destinationValue?.value?.toString() || '';
                  if (rosterType === ReferralMappingRosterType.MEMBER.value) {
                    setMappingRules([...mappingRules]);
                  } else {
                    setCtCandidateMappingRules([...ctCandidateMappingRules]);
                  }
                }
              }}
              value={destinationItems?.find(
                ({ value }) => value.toString() === data.destinationValue,
              )}
              size="small"
            />
            <IconButton
              className={classNames(idx === 0 && styles.firtsItem)}
              disabled={
                operation?.dataDictionary &&
                operation.dataDictionary.length < 2 &&
                !operation.dataDictionary[0]?.sourceValue &&
                !operation.dataDictionary[0]?.destinationValue
              }
              size="small"
              onClick={() => {
                if (operation?.dataDictionary) {
                  if (operation.dataDictionary.length > 1) {
                    operation.dataDictionary?.splice(idx, 1);
                  } else {
                    operation.dataDictionary = [
                      new ReferralMappingMappedValue({}),
                    ];
                  }
                  if (rosterType === ReferralMappingRosterType.MEMBER.value) {
                    setMappingRules([...mappingRules]);
                  } else {
                    setCtCandidateMappingRules([...ctCandidateMappingRules]);
                  }
                }
              }}
            >
              <DeleteOutlineIcon fontSize="large" />
            </IconButton>
          </div>
        ))}
      {!sourceItems?.length && (
        <PanelInfo type="warning">
          {translate('referrals.ingestionMapper.modal.warningNoItems', {
            file: file?.label || 'File',
          })}
        </PanelInfo>
      )}
    </div>
  );

  return (
    <Modal
      scrollRef={scrollRef}
      onClose={onClose}
      open={open}
      title={translate(
        `referrals.ingestionMapper.modal.title${rule ? 'Edit' : 'Add'}`,
        {
          rule: rule?.name || 'Rule',
        },
      )}
      maxWidth="lg"
      onSubmit={doSubmit}
      preventCloseOnBackdropClick
      submitText={translate(`global.${rule ? 'save' : 'add'}`)}
    >
      <div className="grid-wrapper grid-span-12 fit">
        <div className="grid-span-12 flex gap wrap baseline">
          <TextInput
            className={styles.fileInputLarge}
            error={submitted && !name}
            footer={
              <CheckboxWithLabel
                checked={!!active}
                className={styles.footerCheckbox}
                label={translate('common.active')}
                onChange={setActive}
              />
            }
            label={translate('referrals.ingestionMapper.modal.name')}
            onChange={setName}
            value={name}
            required
          />
          <Select
            className={styles.fileInputLarge}
            error={submitted && !referral?.length}
            items={data.organizations}
            footer={
              showMixedFileCheckbox ? (
                <CheckboxWithLabel
                  checked={!!mixedFile}
                  className={styles.footerCheckbox}
                  label={translate('referrals.ingestionMapper.modal.mixedFile')}
                  onChange={setMixedFile}
                />
              ) : undefined
            }
            label={translate('referrals.ingestionMapper.modal.referral')}
            loading={loadingData}
            multiple
            onChange={setReferral}
            value={referral}
            required
          />
          <Select
            className={styles.fileInputLarge}
            error={submitted && !file}
            items={data.files}
            footer={
              <CheckboxWithLabel
                checked={!!duplicateMembers}
                className={styles.footerCheckbox}
                label={translate(
                  'referrals.ingestionMapper.modal.duplicateMembers',
                )}
                tooltip={translate(
                  'referrals.ingestionMapper.modal.duplicateMembersTooltip',
                )}
                onChange={setDuplicateMembers}
              />
            }
            label={translate('referrals.ingestionMapper.modal.file')}
            loading={loadingData || loadingFileHeaders}
            onChange={setFile}
            value={file}
            required
          />
          <TextInput
            className={styles.fileInputSmall}
            error={submitted && !separator}
            label={translate('referrals.ingestionMapper.modal.separator')}
            maxLength={1}
            onChange={(separator) => setSeparator(separator || '')}
            value={separator}
            tooltip={translate(
              'referrals.ingestionMapper.modal.separatorTooltip',
            )}
            required
          />
        </div>
        {errorBranchNotFound && (
          <PanelInfo className="grid-span-12" type="warning">
            {translate('referrals.ingestionMapper.modal.errorBranchNotFound')}
          </PanelInfo>
        )}
        <div className={classNames('grid-span-12', styles.tabsContainer)}>
          <Tabs value={activeTab} items={TABS} onChange={setActiveTab} />
        </div>
        <div className="grid-span-12">
          {activeTab === MODAL_TAB_MEMBER && (
            <>
              {mappingRules?.map((item, idx) => (
                <MappingRuleRow
                  key={`referral-ingestion-rule-${item.id ?? idx}`}
                  item={item}
                  idx={idx}
                  rosterType={ReferralMappingRosterType.MEMBER.value}
                  submitted={submitted}
                  candidateTotal={candidateTotal}
                  memberMapping={{
                    rules: mappingRules,
                    setRules: setMappingRules,
                  }}
                  candidateMapping={{
                    rules: ctCandidateMappingRules,
                    setRules: setCtCandidateMappingRules,
                  }}
                  fileData={{
                    headers: sourceFileHeaders,
                    branches: sourceFileBranches,
                    genders: sourceFileGenders,
                    languages: sourceFileLanguages,
                    booleanColumns: booleanColumnsData,
                    relationshipColumns: relationshipColumnsData,
                  }}
                  file={file}
                  referral={referral}
                  mixedReferrals={mixedReferrals}
                  mixedFile={mixedFile}
                  loading={{
                    data: loadingData,
                    fileBranches: loadingFileBranchValues,
                    fileGenders: loadingFileGenderValues,
                    fileLanguages: loadingFileLanguageValues,
                    booleanColumns: loadingBooleanColumns,
                    relationshipColumns: loadingRelationshipColumns,
                  }}
                  destinationColumns={data.destinationColumns}
                  relationships={relationships}
                  specialColumns={{
                    branch: branchColumn,
                    gender: genderColumn,
                    language: languageColumn,
                  }}
                  updateBooleanFileValues={(
                    sourceColumn,
                    sourceColumnIndex,
                    modelColumn,
                  ) =>
                    updateMultipleFileValues(
                      sourceColumn,
                      sourceColumnIndex,
                      modelColumn,
                      setLoadingBooleanColumns,
                      setBooleanColumnsData,
                      ReferralMappingOperationType.BOOLEAN,
                      file,
                    )
                  }
                  updateRelationshipFileValues={(
                    sourceColumn,
                    sourceColumnIndex,
                    modelColumn,
                  ) =>
                    updateMultipleFileValues(
                      sourceColumn,
                      sourceColumnIndex,
                      modelColumn,
                      setLoadingRelationshipColumns,
                      setRelationshipColumnsData,
                      ReferralMappingOperationType.RELATIONSHIP,
                      file,
                    )
                  }
                  renderMappingOptions={renderMappingOptions}
                />
              ))}
            </>
          )}

          {activeTab === MODAL_TAB_CANDIDATE && (
            <div>
              {!hasMemberIdentificationFields ? (
                <PanelInfo type="warning" icon={<InfoIcon />}>
                  {translate(
                    'referrals.ingestionMapper.modal.candidateMappingError',
                  )}
                  <Button
                    size="small"
                    onClick={() => setActiveTab(MODAL_TAB_MEMBER)}
                  >
                    OK
                  </Button>
                </PanelInfo>
              ) : (
                <>
                  <div
                    className="grid-span-12 flex gap wrap baseline"
                    style={{ margin: '1rem' }}
                  >
                    <TextInput
                      className={styles.fileInputSmall}
                      error={submitted && candidateTotal < 0}
                      label={translate(
                        'referrals.ingestionMapper.modal.candidateTotal',
                      )}
                      onChange={(value) => {
                        const numValue = parseInt(value || '0', 10);
                        if (!isNaN(numValue) && numValue >= 0) {
                          setCandidateTotal(numValue);
                        }
                      }}
                      value={candidateTotal?.toString()}
                      type="number"
                    />
                  </div>

                  {candidateValidationErrors.length > 0 && submitted && (
                    <div style={{ margin: '1rem 0' }} className="grid-span-12">
                      <PanelInfo type="warning">
                        <div>
                          <strong>Candidate Mapping Errors:</strong>
                          <ul
                            style={{
                              margin: '0.5rem 0',
                              paddingLeft: '1.5rem',
                            }}
                          >
                            {candidateValidationErrors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </PanelInfo>
                    </div>
                  )}

                  {ctCandidateMappingRules?.map((item, idx) => (
                    <MappingRuleRow
                      key={`ct-candidate-rule-${item.id ?? idx}`}
                      item={item}
                      idx={idx}
                      rosterType={ReferralMappingRosterType.CANDIDATE.value}
                      submitted={submitted}
                      candidateTotal={candidateTotal}
                      memberMapping={{
                        rules: mappingRules,
                        setRules: setMappingRules,
                      }}
                      candidateMapping={{
                        rules: ctCandidateMappingRules,
                        setRules: setCtCandidateMappingRules,
                      }}
                      fileData={{
                        headers: sourceFileHeaders,
                        branches: sourceFileBranches,
                        genders: sourceFileGenders,
                        languages: sourceFileLanguages,
                        booleanColumns: booleanColumnsData,
                        relationshipColumns: relationshipColumnsData,
                      }}
                      file={file}
                      referral={referral}
                      mixedReferrals={mixedReferrals}
                      mixedFile={mixedFile}
                      loading={{
                        data: loadingData,
                        fileBranches: loadingFileBranchValues,
                        fileGenders: loadingFileGenderValues,
                        fileLanguages: loadingFileLanguageValues,
                        booleanColumns: loadingBooleanColumns,
                        relationshipColumns: loadingRelationshipColumns,
                      }}
                      destinationColumns={data.destinationColumns}
                      relationships={relationships}
                      specialColumns={{
                        branch: branchColumn,
                        gender: genderColumn,
                        language: languageColumn,
                      }}
                      updateBooleanFileValues={(
                        sourceColumn,
                        sourceColumnIndex,
                        modelColumn,
                      ) =>
                        updateMultipleFileValues(
                          sourceColumn,
                          sourceColumnIndex,
                          modelColumn,
                          setLoadingBooleanColumns,
                          setBooleanColumnsData,
                          ReferralMappingOperationType.BOOLEAN,
                          file,
                        )
                      }
                      updateRelationshipFileValues={(
                        sourceColumn,
                        sourceColumnIndex,
                        modelColumn,
                      ) =>
                        updateMultipleFileValues(
                          sourceColumn,
                          sourceColumnIndex,
                          modelColumn,
                          setLoadingRelationshipColumns,
                          setRelationshipColumnsData,
                          ReferralMappingOperationType.RELATIONSHIP,
                          file,
                        )
                      }
                      renderMappingOptions={renderMappingOptions}
                    />
                  ))}
                </>
              )}
            </div>
          )}

          {((activeTab === MODAL_TAB_MEMBER &&
            mappingRules?.length < data.destinationColumns?.length) ||
            (activeTab === MODAL_TAB_CANDIDATE &&
              hasMemberIdentificationFields &&
              ctCandidateMappingRules?.filter((rule) => !rule.readonly)
                ?.length < availableCtSourceColumns?.length)) && (
            <div className={classNames('flex center', styles.row)}>
              <Button
                className={styles.button}
                color="tertiary"
                icon={<AddIcon />}
                onClick={() => {
                  if (activeTab === MODAL_TAB_MEMBER) {
                    setMappingRules([
                      ...mappingRules,
                      {
                        rosterType: ReferralMappingRosterType.MEMBER.value,
                      } as Rule,
                    ]);
                  } else {
                    setCtCandidateMappingRules([
                      ...ctCandidateMappingRules,
                      {
                        rosterType: ReferralMappingRosterType.CANDIDATE.value,
                      } as Rule,
                    ]);
                  }
                }}
              >
                {translate('referrals.ingestionMapper.modal.addRule')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ReferralIngestionMapperEditModal;
